import { COLOR_SWATCH_DIFFERENCE_THRESHOLD, PRIMARY_COLOR_SWATCH_INDEX, SECONDARY_COLOR_SWATCH_INDEX, SIMILARITY_COLOR_SWATCH_RATIO } from '~/app/constants'
import type { GalleryContentQuery } from 'shared/types/gallery/content-query'

interface GetColorSwatchColorsResult {
  primaryColor: string
  secondaryColor?: string
  tertiaryColor?: string
}

const rgbToHex = (hex: string): GalleryContentQuery.RGB => {
  const color = hex.replace(/^#/, '')

  const r = parseInt(color.substring(0, 2), 16)
  const g = parseInt(color.substring(2, 4), 16)
  const b = parseInt(color.substring(4, 6), 16)

  return { r, g, b }
}

const colorDelta = (color1: string, color2: string): number => {
  const rgb1 = rgbToHex(color1)
  const rgb2 = rgbToHex(color2)

  return Math.sqrt((rgb1.r - rgb2.r) ** 2 + (rgb1.g - rgb2.g) ** 2 + (rgb1.b - rgb2.b) ** 2)
}

export const compareColorSwatchColors = (colorSwatchObjects: GalleryContentQuery.ColorSwatch[], index: number): boolean => {
  const colorsSwatchColorByIndex = colorSwatchObjects.map((colorSwatch) => colorSwatch?.colorComposition?.[index]?.color)

  let similarCount = 0
  const threshold = Math.floor(colorsSwatchColorByIndex.length * SIMILARITY_COLOR_SWATCH_RATIO)

  colorsSwatchColorByIndex.forEach((color, i) => {
    if (i < colorsSwatchColorByIndex.length - 1) {
      const color1 = color
      const color2 = colorsSwatchColorByIndex[i + 1]

      if (color1 && color2) {
        const delta = colorDelta(color1, color2)

        if (delta < COLOR_SWATCH_DIFFERENCE_THRESHOLD) {
          similarCount += 1
        }
      }
    }
  })

  return similarCount >= threshold
}

export const getIsColorSwatchesSimilarColors = (colorSwatchObjects: GalleryContentQuery.ColorSwatch[]): boolean => {
  // Similarity adjustment applies only to native palette swatches.
  // Custom color variations (from DPC) don't carry meaningful colorComposition
  // and should not influence the similarity calculation.
  const nativeSwatches = colorSwatchObjects.filter((cs) => !cs.isCustomColorVariation)

  if (nativeSwatches.length < 2) return false

  const primaryColorSwatchResult = compareColorSwatchColors(nativeSwatches, PRIMARY_COLOR_SWATCH_INDEX)
  const secondaryColorSwatchResult = compareColorSwatchColors(nativeSwatches, SECONDARY_COLOR_SWATCH_INDEX)

  return primaryColorSwatchResult && secondaryColorSwatchResult
}

/**
 * Sorts custom color variation swatches by the number of colors they contain:
 * single-color first, then double-color, then triple-color (ascending by colorComposition length).
 * Returns a new array; does not mutate the input.
 *
 * TODO(DIYDR-1995): This client-side sorting is a workaround because Design Conductor does not
 * guarantee a meaningful swatch order when multiple personalized colors are active.
 * Once DIYDR-1995 is resolved and DC returns swatches in the desired order, remove this
 * function and update `filterPersonalizedColorSwatches` to stop sorting.
 * See: https://vistaprint.atlassian.net/browse/DIYDR-1995
 */
export const sortCustomColorSwatches = (swatches: GalleryContentQuery.ColorSwatch[]): GalleryContentQuery.ColorSwatch[] =>
  [...swatches].sort((a, b) => (a.colorComposition?.length ?? 1) - (b.colorComposition?.length ?? 1))

/**
 * When color personalization is active, filters to custom variation swatches only
 * and sorts them by color count. When inactive, returns the original array unchanged.
 */
export const filterPersonalizedColorSwatches = (
  colorSwatches: GalleryContentQuery.ColorSwatch[],
  personalizationHasColor: boolean,
): GalleryContentQuery.ColorSwatch[] =>
  personalizationHasColor ? sortCustomColorSwatches(colorSwatches.filter((cs) => cs.isCustomColorVariation)) : colorSwatches

export const getColorSwatchColors = (
  colorComposition: GalleryContentQuery.ColorComposition[],
  isColorSwatchColorsSimilar?: boolean,
): GetColorSwatchColorsResult => {
  const [primaryColor, secondaryColor, thirdColor] = colorComposition.map(({ color }) => color)

  const changedSecondaryColor = isColorSwatchColorsSimilar ? thirdColor : secondaryColor

  return {
    primaryColor,
    secondaryColor: changedSecondaryColor,
    tertiaryColor: isColorSwatchColorsSimilar ? undefined : thirdColor,
  }
}
